import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Inbox, Clock, CheckCircle2, CalendarDays, Loader2,
  ExternalLink, RefreshCw, MessageSquare, TrendingUp, Activity,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { BookingsPanel, STATUS, NEXT } from "../components/admin/BookingsPanel";
import { ReviewsPanel } from "../components/admin/ReviewsPanel";
import { SearchesPanel } from "../components/admin/SearchesPanel";
import { ActivityPanel } from "../components/admin/ActivityPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };
const PAGE = 50;

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [topSearches, setTopSearches] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("bookings");

  const load = useCallback(async () => {
    try {
      const [s, b, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, cfg),
        axios.get(`${API}/admin/bookings?limit=${PAGE}&skip=0`, cfg),
        axios.get(`${API}/admin/reviews`, cfg),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setHasMore(b.data.length === PAGE);
      setReviews(r.data);
      axios.get(`${API}/admin/top-searches`, cfg).then((t) => setTopSearches(t.data)).catch(() => {});
      axios.get(`${API}/admin/activity`, cfg).then((a) => setActivity(a.data)).catch(() => {});
    } catch {
      toast.error("Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/admin/bookings?limit=${PAGE}&skip=${bookings.length}`, cfg);
      setBookings((prev) => [...prev, ...res.data]);
      setHasMore(res.data.length === PAGE);
    } catch {
      toast.error("Could not load more.");
    } finally {
      setLoadingMore(false);
    }
  };

  const cycleStatus = async (bk) => {
    const next = NEXT[bk.status] || "new";
    try {
      const res = await axios.patch(`${API}/admin/bookings/${bk.id}`, { status: next }, cfg);
      setBookings((prev) => prev.map((x) => (x.id === bk.id ? res.data : x)));
      const s = await axios.get(`${API}/admin/stats`, cfg);
      setStats(s.data);
      toast.success(`Marked as ${STATUS[next].label}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (bk) => {
    if (!window.confirm(`Delete booking from ${bk.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bk.id}`, cfg);
      setBookings((prev) => prev.filter((x) => x.id !== bk.id));
      const s = await axios.get(`${API}/admin/stats`, cfg);
      setStats(s.data);
      toast.success("Booking deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const toggleApprove = async (rv) => {
    try {
      const res = await axios.patch(`${API}/admin/reviews/${rv.id}`, { approved: !rv.approved }, cfg);
      setReviews((prev) => prev.map((x) => (x.id === rv.id ? res.data : x)));
      toast.success(res.data.approved ? "Review published." : "Review hidden.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeReview = async (rv) => {
    if (!window.confirm(`Delete review from ${rv.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/reviews/${rv.id}`, cfg);
      setReviews((prev) => prev.filter((x) => x.id !== rv.id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const visible = bookings.filter((b) => {
    const okFilter = filter === "all" || b.status === filter;
    const okQ =
      !q ||
      b.name.toLowerCase().includes(q.toLowerCase()) ||
      b.email.toLowerCase().includes(q.toLowerCase()) ||
      (b.service || "").toLowerCase().includes(q.toLowerCase());
    return okFilter && okQ;
  });

  const pendingReviews = reviews.filter((r) => !r.approved).length;

  const cards = [
    { key: "total", label: "Total bookings", icon: Inbox, value: stats?.total },
    { key: "new", label: "New / unread", icon: Clock, value: stats?.new },
    { key: "this_week", label: "This week", icon: CalendarDays, value: stats?.this_week },
    { key: "completed", label: "Completed", icon: CheckCircle2, value: stats?.completed },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-secondary">
      {/* Top bar */}
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-muted-foreground font-semibold hover:text-accent transition-colors"
            >
              <ExternalLink className="h-4 w-4" /> View site
            </Link>
            <span className="hidden md:block text-sm text-muted-foreground">{user?.email}</span>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-white font-bold hover:bg-slate-800 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-foreground tracking-tight">
              Dashboard
            </h1>
            <p className="mt-2 text-muted-foreground text-lg">
              Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Here's what's come in.
            </p>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border bg-card font-semibold text-foreground hover:border-accent transition-colors"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center justify-between">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/10 text-accent">
                  <c.icon className="h-6 w-6" />
                </span>
                <span className="font-heading font-extrabold text-3xl text-foreground">
                  {stats ? c.value : "—"}
                </span>
              </div>
              <p className="mt-4 text-muted-foreground font-medium">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2">
          <button
            data-testid="tab-bookings"
            onClick={() => setTab("bookings")}
            className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold transition-colors ${
              tab === "bookings" ? "bg-primary text-white" : "bg-card border border-border text-slate-300 hover:border-accent"
            }`}
          >
            <Inbox className="h-4 w-4" /> Bookings
          </button>
          <button
            data-testid="tab-reviews"
            onClick={() => setTab("reviews")}
            className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold transition-colors ${
              tab === "reviews" ? "bg-primary text-white" : "bg-card border border-border text-slate-300 hover:border-accent"
            }`}
          >
            <MessageSquare className="h-4 w-4" /> Reviews
            {pendingReviews > 0 && (
              <span className="grid place-items-center h-6 min-w-6 px-1.5 rounded-full bg-accent text-white text-xs font-bold">
                {pendingReviews}
              </span>
            )}
          </button>
          <button
            data-testid="tab-searches"
            onClick={() => setTab("searches")}
            className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold transition-colors ${
              tab === "searches" ? "bg-primary text-white" : "bg-card border border-border text-slate-300 hover:border-accent"
            }`}
          >
            <TrendingUp className="h-4 w-4" /> Popular Searches
          </button>
          <button
            data-testid="tab-activity"
            onClick={() => setTab("activity")}
            className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold transition-colors ${
              tab === "activity" ? "bg-primary text-white" : "bg-card border border-border text-slate-300 hover:border-accent"
            }`}
          >
            <Activity className="h-4 w-4" /> Activity
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-accent" />
          </div>
        ) : tab === "bookings" ? (
          <BookingsPanel
            visible={visible}
            filter={filter}
            setFilter={setFilter}
            q={q}
            setQ={setQ}
            hasMore={hasMore}
            loadingMore={loadingMore}
            loadMore={loadMore}
            onCycle={cycleStatus}
            onRemove={remove}
          />
        ) : tab === "reviews" ? (
          <ReviewsPanel
            reviews={reviews}
            onToggleApprove={toggleApprove}
            onRemoveReview={removeReview}
          />
        ) : tab === "searches" ? (
          <SearchesPanel topSearches={topSearches} />
        ) : (
          <ActivityPanel items={activity} />
        )}
      </main>
    </div>
  );
}
